/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

/**
 * Created by randysorensen on 1/31/17.
 */

//
// RequirementList
//
// Given:
//  * a list of N objects that look something like this:
//      {
//          widget: { id: 1, ... },
//          required: true
//      }
//
//  * an array with any number 0..N of these objects, with
//    inclusion implying that the object is enabled
//
// This component creates a grid where objects can be added to
// the enabled list by selecting grid rows, and where objects
// can be marked as required in a column of checkboxes.
//
// The name of the properties in the object are configurable
// by binding, as is the name of the object's class (which will
// show up in the list header).
//
// See the bindings for the complete list of configurable options.
//
// The general concept is to allow the user to indicate that
// selections from a list are either not applicable, optional
// or required.
//

function RequirementListController (
    this: JS5Object,
    _: JS5Object,
    $attrs: JS5Object,
    $q: JS5Object,
    $scope: JS5Object,
    uiGridConstants: JS5Object
) {
    const enabledHeaderTemplate = 'components/visual/grid/header-check-mark-component-template.html';
    const requiredHeaderTemplate = 'components/visual/requirementList/gridCells/required-column-header-cell-template.html';
    const requiredCellTemplate = 'components/visual/requirementList/gridCells/required-column-grid-cell-template.html';

    let gridApiPromise = $q.defer();

    this.disabled = $scope.$eval($attrs.ngDisabled);

    this.gridOptions = {
        rowSelection: true,
        enableRowHeaderSelection: false,
        enableSelectAll: true,
        enableSorting: true,
        enableColumnMenus: false,
        enableFullRowSelection: true,
        rowHeight: 26,
        multiSelect: true,
        onRegisterApi: (gridApi: JS5Object) => {
            this.gridApi = gridApi;
            this.gridApi.selection.on.rowSelectionChanged(null, this.onEntityEnableClicked);

            // this.gridApi may now be used.
            gridApiPromise.resolve();
        },
        columnDefs: [
            {	// Enabled checkbox column
                name: 'checkMarkRowHeaderCol',
                field: 'checkMark',
                width: 30,
                enableColumnMenu: false,
                enableSorting: false,
                enableColumnResizing: false,
                headerCellTemplate: enabledHeaderTemplate,
                cellTemplate: 'components/visual/grid/check-mark-template.html'
            },
            {	// Option label column
                name: 'label',
                width: 300,
                enableSorting: false,
                displayName: this.entityName,
                cellClass: 'ui-grid-cell-contents',
                cellTemplate: '<div class="padding-left-sm">{{row.entity.label}}</div>',
                enableColumnResizing: false,
                sort: {
                    direction: uiGridConstants.ASC,
                    priority: 0
                },
            },
            {	// "Required" checkbox column
                name: 'required',
                width: 100,
                enableSorting: false,
                displayName: 'Required',
                cellClass: 'ui-grid-cell-contents',
                headerCellTemplate: requiredHeaderTemplate,
                cellTemplate: requiredCellTemplate,
                enableColumnResizing: false
            }
        ],
        data: []
    };


    //
    // Functions to convert grid data into DTO-friendly data
    //

    const findExistingEntityId = (entityName: string) => {
        let existingEntity = _.find(this.originalEnabledEntities, {
            [this.entityProp]: {
                name: entityName
            }
        });

        return _.get(existingEntity, 'id', null);
    };

    const buildEntityFromGridRow = (row: JS5Object) => {
        let entity = _.find(this.allEntities, {
            name: row.label
        });

        let newEnabledEntity: JS5Object = {
            [this.entityProp]: entity,
            [this.requiredFlagProp]: row.required
        };

        let existingEntityId = findExistingEntityId(row.label);
        if (existingEntityId) {
            newEnabledEntity.id = existingEntityId;
        }

        return newEnabledEntity;
    };

    const buildEnabledEntitiesList = () => {
        let enabledEntities: JS5Object[] = [];
        _.forEach(this.gridApi.selection.getSelectedRows(), (row: JS5Object) => {
            enabledEntities.push(buildEntityFromGridRow(row));
        });
        return enabledEntities;
    };

    const dataChanged = () => {
        this.enabledEntities = buildEnabledEntitiesList();
        _.invoke(this, 'onUpdate', {
            context: this.context,
            newEnabledEntities: this.enabledEntities
        });
    };


    //
    // Event handlers
    //

    this.onEntityEnableClicked = (row: JS5Object, $event: JS5Object) => {
        if (!row.isSelected) {
            row.entity.required = false;
        }

        if ($event) {
            dataChanged();
        }
    };

    this.onEntityRequireClicked = (row: JS5Object, $event: JS5Object) => {
        $event.stopPropagation();
        row.isSelected = true;
        row.entity.required = !row.entity.required;
        dataChanged();
    };

    this.areAllEntitiesRequired = () => {
        return this.gridOptions.data.reduce((allChecked: boolean, row: JS5Object) => {
            return allChecked && row.required;
        }, this.gridOptions.data.length > 0);
    };

    this.onSelectAllToggled = (event: JS5Object) => {
        if (this.gridApi.selection.getSelectAllState()) {
            this.gridApi.selection.clearSelectedRows(event);
            _.forEach(this.gridOptions.data, (row: JS5Object) => {
                row.required = false;
            });
        } else {
            this.gridApi.selection.selectAllRows();
        }
        dataChanged();
    };

    this.onRequireAllClicked = () => {
        var newVal = !this.areAllEntitiesRequired();
        _.forEach(this.gridOptions.data, (row: JS5Object) => {
            row.required = newVal;
            if (newVal) {
                this.gridApi.grid.modifyRows(this.gridOptions.data);
                this.gridApi.selection.selectRow(row);
            }
        });
        dataChanged();
    };


    //
    // functions to take DTO-formed data and assemble
    // grid-friendly data
    //

    const isEnabledAndRequired = (entityName: string, required: boolean=undefined) => {
        //
        // Returns true if the entity with the specified name is
        // enabled; if the second argument is true, this only
        // returns true if the named entity is also required.
        //

        let criteria: JS5Object = {};
        criteria[this.entityProp] = {
            name: entityName
        };

        if (required !== undefined) {
            criteria[this.requiredFlagProp] = required;
        }

        return _.find(this.enabledEntities, criteria) !== undefined;
    };

    const buildGridRow = (entity: JS5Object) => {
        return {
            label: entity.name,
            required: isEnabledAndRequired(entity.name, true)
        };
    };

    const buildGridData = () => {
        this.gridOptions.data = [];
        _.forEach(this.allEntities, (entity: JS5Object) => {
            this.gridOptions.data.push(buildGridRow(entity));
        });
    };

    const selectEnabledEntityRows = () => {
        _.forEach(this.gridOptions.data, (row: JS5Object) => {
            if (isEnabledAndRequired(row.label)) {
                this.gridApi.grid.modifyRows(this.gridOptions.data);
                this.gridApi.selection.selectRow(row);
            }
        });
    };

    const updateGridData = () => {

        //
        // Wait until the gridApi is registered before selecting
        // rows with it.
        //

        gridApiPromise.promise.then(() => {
            if (this.enabledEntities && this.allEntities) {
                this.originalEnabledEntities = _.cloneDeep(this.enabledEntities);
                buildGridData();
                selectEnabledEntityRows();
                this.gridApi.core.refresh();
            }
        });
    };

    this.$onChanges = () => {
        updateGridData();
    };

    updateGridData();
}

angular.module('prism-ui.components').component('requirementList', {
    templateUrl: 'components/visual/requirementList/requirement-list-template.html',
    controller: RequirementListController,
    bindings: {
        // all data object choices
        allEntities: '<',

        // arbitrary/user-defined context - passed to onUpdate
        context: '<',

        // set of enabled data object choices and whether they're required
        enabledEntities: '<',

        // the title above the first column, e.g. "Video Role" or "Language"
        entityName: '@',

        // the property name that the data object is stored by, e.g. 'language' or 'role'
        entityProp: '@',

        // callback signaling data change to parent
        onUpdate: '&',

        // the property name that the required flag is stored by, e.g. 'required' or 'isRequired'
        requiredFlagProp: '@'
    }

    //
    // Note: the 'enabledEntities' binding is one-directionally
    // bound, however as it's an object reference, changes to the
    // object's properties will be visible to the parent
    // controller.
    //
});
